<template>
  <div class="upsell-features w-full" data-testid="upsell-feature-grid">
    <div
      v-for="feature in features"
      :key="feature.title"
      :class="[
        'upsell-features__card',
        `upsell-features__card--${feature.key}`,
      ]"
      :data-testid="`upsell-feature-card-${toTestId(feature.title)}`"
    >
      <div class="upsell-features__copy">
        <div class="upsell-features__header">
          <span class="upsell-features__icon" aria-hidden="true">
            <component :is="feature.icon ?? SparklesSoft" :size="20" />
          </span>
          <OwnType
            :text="feature.title"
            variant="title-small"
            color="secondary"
          />
        </div>
        <p class="upsell-features__body">{{ feature.description }}</p>
      </div>

      <div
        :class="[
          'upsell-features__art',
          `upsell-features__art--${feature.key}`,
        ]"
      >
        <img
          class="upsell-features__image"
          :src="CARD_ART[feature.key]"
          alt=""
          :data-testid="`upsell-feature-image-${feature.key}`"
        />
        <span
          v-if="feature.key === 'app' && appIconUrl"
          class="upsell-features__app-icon"
          :style="{ backgroundColor: appIconBackgroundColor }"
          data-testid="upsell-feature-personalized-app-icon"
        >
          <img :src="appIconUrl" alt="" />
        </span>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { SparklesSoft } from '@owner/icons-vue/SparklesSoft'

import { OwnType } from '@/ui/OwnType'

import brandedAppArt from '../assets/branded-app.png'
import reviewsArt from '../assets/reviews.png'
import type { UpsellFeature, UpsellFeatureKey } from '../types'
import { toTestId } from '../utils'

const CARD_ART: Record<UpsellFeatureKey, string> = {
  app: brandedAppArt,
  reviews: reviewsArt,
}

defineProps<{
  appIconBackgroundColor?: string
  appIconUrl?: string | null
  features: UpsellFeature[]
}>()
</script>

<style lang="scss" scoped>
@use '@/styles/breakpoints';

// The fixed palette comes from the design and is not yet in Hermes tokens.
.upsell-features {
  display: flex;
  flex-direction: column;
  gap: 24px;

  &__card {
    display: flex;
    flex-direction: column;
    gap: 12px;
    overflow: hidden;
    border: 0.5px solid rgb(202 200 217 / 50%);
    border-radius: 16px;
    background-color: #f7f8fa;
  }

  &__copy {
    display: flex;
    flex-direction: column;
    gap: 10px;
    padding: 24px 24px 0;
  }

  &__header {
    display: flex;
    gap: 6px;
    align-items: center;
    color: var(--text-color-secondary);
  }

  &__icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
  }

  &__body {
    margin: 0;
    color: var(--text-color-primary);
    font-size: 18px;
    font-weight: 500;
    line-height: 25.2px;
  }

  // Artwork region ratios come from the Figma card specs, not the PNGs, so the
  // crop matches the design at any card width.
  &__art {
    position: relative;
    margin-top: auto;
    width: 100%;

    &--app {
      aspect-ratio: 360 / 252;
    }

    &--reviews {
      aspect-ratio: 360 / 196;
    }
  }

  &__image {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  &__app-icon {
    position: absolute;
    top: 60%;
    left: 39.9%;
    display: grid;
    width: 21.5%;
    height: 27%;
    place-items: center;
    overflow: hidden;
    border-radius: 20%;

    img {
      width: 67%;
      height: 67%;
      object-fit: contain;
    }
  }

  @include breakpoints.respond-to('md-and-up') {
    display: grid;
    grid-template-columns: 1fr 1fr;

    &__card {
      border-radius: 24px;
    }

    &__card--app,
    &__card--reviews {
      position: relative;
      flex-direction: row;
      align-items: center;
      grid-column: 1 / -1;
      min-height: 260px;
      padding: 32px;
    }

    &__copy {
      padding: 0;
    }

    &__card--app .upsell-features__copy {
      max-width: 55%;
    }

    &__card--reviews .upsell-features__copy {
      max-width: 55%;
    }

    &__body {
      font-size: 20px;
      line-height: 26.6px;
    }

    &__art--app {
      position: absolute;
      inset: 0 0 0 auto;
      margin: 0;
      width: auto;
      height: 100%;
      aspect-ratio: 646 / 512;
    }

    // Full-bleed to the card edges, past the 32px padding.
    &__art--reviews {
      position: absolute;
      right: 0;
      bottom: 0;
      margin: 0;
      width: 48%;
      height: auto;
      aspect-ratio: 988 / 512;
    }
  }
}
</style>
